import { List, Lock } from "@mui/icons-material";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../components";
import { BoardLayout } from "../../components/boardLayout";
import { IBoard, IItem } from "../../components/boardLayout/types";
import { withNavBars } from "../../HOCs";
import { useToggleView } from "../../hooks";
import { TagInfo } from "../../interfaces";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useLazyGetProductsQuery, useLazyGetTagGroupsQuery, useLazyGetTagsBasedOnParameterQuery, useUpdateProductMutation } from "../../redux/services";
import { resetProductSlice } from "../../redux/slices/product";
import { HasAccess } from "../../router/authorization";
import { PERMISSIONS } from "../../router/permission";
import { AppRoutes } from "../../router/routes";
import { DEFAULT_BOARD_VIEW_PAGINATION } from "../../utils";
import { Dashboard } from "./dashboard";

const Products: React.FC<{ children?: JSX.Element }> = (props) => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { perm } = useAppSelector(store => store.auth.userDetails);
    const { isBoardView, selectedBoard } = useToggleView("product");

    const [getAllProducts] = useLazyGetProductsQuery({ refetchOnFocus: true });
    const [getTagGroups, tagGroupResponse] = useLazyGetTagGroupsQuery();
    const [getTags, tagsResponse] = useLazyGetTagsBasedOnParameterQuery();
    const [updateProduct] = useUpdateProductMutation();

    const [search, setSearch] = React.useState("");

    const isLoading = tagGroupResponse.isFetching || tagGroupResponse.isLoading;

    const creatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.PRODUCT_CREATE), [perm]);

    const addEditProduct = (id?: string) => {
        dispatch(resetProductSlice())
        navigate(AppRoutes.viewProductBasicDetail(id ?? "New"))
    };

    const boardTypes = (tagsResponse?.data?.tags ?? []).map((t: any) => ({
        ...t, id: t._id, title: t.tag_name, description: t.tag_desc
    }));

    const refetchTagGroups = () => {
        getTagGroups({ ...DEFAULT_BOARD_VIEW_PAGINATION, scope: "product" });
    };

    React.useEffect(() => {
        if (isBoardView) {
            refetchTagGroups();
        }
        // eslint-disable-next-line
    }, [isBoardView]);

    React.useEffect(() => {
        if (selectedBoard?.canonical_name && tagGroupResponse.isSuccess) {
            getTags(selectedBoard?.canonical_name);
        }
        // eslint-disable-next-line
    }, [selectedBoard?._id, isLoading]);

    return <Box sx={{ padding: 1, height: "100%" }}>
        {isBoardView
            ?
            <BoardLayout
                layoutTitleDetails={{
                    showTitle: false,
                    title: "Products",
                    breadcrumbs: [
                        { id: crypto.randomUUID(), label: "Product Module", path: '' }
                    ],
                    currentPath: "Products"
                }}
                boardViewDetails={{
                    isLoading,
                    entityType: "product",
                    boards: boardTypes,
                    refetchBoards: refetchTagGroups,
                    onClick: (board: IBoard, item: IItem) => {
                        if (item?._id) {
                            addEditProduct(item._id);
                        }
                    },
                    getTitle(d: IItem) {
                        return (
                            <Stack direction="row" sx={{ alignItems: "center" }}>
                                <Typography
                                    id="view-product"
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ cursor: "pointer" }}>
                                    {d.title}
                                </Typography>

                                {d?.user_access && <Tooltip title={`This ${d.title} is private`}>
                                    <Lock color='disabled' fontSize={"small"} sx={{ fontSize: "14px", marginLeft: "4px", marginTop: "-2px !important" }} />
                                </Tooltip>}
                            </Stack>
                        );
                    },
                    searchDetails: {
                        search,
                        onSearch: setSearch,
                        searchPlaceholder: "Search for Products"
                    },
                    tagFilterOption: { scope: "product" },
                    creatable,
                    createBtnName: "Add Product",
                    onCreateBtn: () => addEditProduct(),
                    fetchBoardItems: async (board: IBoard, page: number, rowsPerPage: number, search: string, filters) => {
                        const { data: boardData = { data: [], totalCount: 0 }, ...rest } = await getAllProducts({ search: search.trim() ? search : "", page: page - 1, pageSize: rowsPerPage, tag_id: [board.id, ...filters?.tags?.map((t) => t.value) ?? []].join(',') });
                        return { ...boardData, ...rest };
                    },
                    onUpdateItemBoard: async (result, item: any) => {
                        const dropTag = boardTypes.find((b: IBoard) => b?.id === result?.destination?.droppableId);
                        if (dropTag) {
                            const updatedItem = {
                                product_name: item?.product_name,
                                family_id: item?.family_id,
                                product_manager: item?.product_manager,
                                product_description: item?.product_description,
                                group_id: item?.group_id,
                                product_noun_id: item?.product_noun_id,
                                tags_info: [...(item.tags_info ?? []).filter((t: TagInfo) => t.tag_group_canonical_name !== (boardTypes?.[0] as any)?.tag_group_canonical_name), dropTag].map((t) => ({ tag_group_id: t.tag_group_id, tag_id: t.tag_id ?? t._id }))
                            }
                            return await updateProduct({ id: item?._id, update_version: false, payload: updatedItem as any, skipDataManipulation: true });
                        }
                        return new Promise((resolve, reject) => {
                            reject({ error: "Unable to move the product" });
                        })
                    },
                }}
            />
            :
            <Layout
                history={[
                    { label: "Product Module" }
                ]}
                currentPath={"Products"}
                hideNavBarByDefault={true}
                navBars={[{
                    id: "1",
                    title: "Products",
                    icon: <List fontSize="small" />,
                    path: AppRoutes.products
                },]}
                mainPanel={<Dashboard />}
                sideNavVariant={"whiteboard"}
            />
        }
    </Box>
}

export default withNavBars(Products);